import axios from "axios";
import { instance } from "../instance";
import { PATH } from "../../shared/constants";
import { normalizeOperatorEmail } from "../../utils/operatorEmail";
import { IAxiosError } from "../interfaces";
import { getError } from "../utils";

export type ClientTrackerStatusName =
  | "Needs Attention"
  | "Developing"
  | "On Track"
  | "Strong"
  | "At Risk";

export interface ClientTrackerOverviewCard {
  teamId: string;
  teamName: string;
  teamLogoUrl?: string;
  leagueName: string;
  stadiumName?: string;
  primaryRepresentative: { email: string; name: string } | null;
  supportingRepresentatives: Array<{ email: string; name: string }>;
  lastClientContactAt: string | null;
  calculatedStatus: ClientTrackerStatusName;
  manualOverride: ClientTrackerStatusName | null;
  displayStatus: ClientTrackerStatusName;
  statusColor: string;
  statusIsManualOverride: boolean;
  staleContactWarning: boolean;
}

export interface ClientTrackerRepOption {
  email: string;
  name: string;
}

export function clientTrackerDetailPath(teamId: string): string {
  return `/client-tracker/${encodeURIComponent(teamId)}`;
}

export async function listClientTrackerOverview(
  viewerEmail: string,
  search?: string,
): Promise<{ clients: ClientTrackerOverviewCard[] }> {
  const email = normalizeOperatorEmail(viewerEmail);
  const params = new URLSearchParams({ email });
  if (search?.trim()) params.set("search", search.trim());
  const { data } = await instance.get<{ clients: ClientTrackerOverviewCard[] }>(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker?${params.toString()}`,
  );
  return data;
}

export interface ClientTrackerAddableTeam {
  teamId: string;
  name: string;
  isMomentoClient: boolean;
}

export async function listClientTrackerAddableTeams(
  viewerEmail: string,
): Promise<ClientTrackerAddableTeam[]> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.get<{ teams: ClientTrackerAddableTeam[] }>(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/addable-teams?email=${encodeURIComponent(email)}`,
  );
  return data.teams || [];
}

export async function enableClientTrackerTeam(
  teamId: string,
  viewerEmail: string,
): Promise<{ teamId: string; teamName: string }> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.post<{ teamId: string; teamName: string }>(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/enable`,
    { teamId, email },
  );
  return data;
}

export async function getClientTrackerRepOptions(): Promise<ClientTrackerRepOption[]> {
  const { data } = await instance.get<{ reps: ClientTrackerRepOption[] }>(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/rep-options`,
  );
  return data.reps || [];
}

export async function getClientTrackerDetail(
  teamId: string,
  viewerEmail: string,
): Promise<any> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.get(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}?email=${encodeURIComponent(email)}`,
  );
  return data;
}

export async function updateClientTrackerClientInfo(
  teamId: string,
  viewerEmail: string,
  payload: Record<string, unknown>,
): Promise<any> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.put(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/client-info`,
    { ...payload, email },
  );
  return data;
}

export async function upsertClientTrackerOperatorInstructionImage(
  teamId: string,
  viewerEmail: string,
  opts: {
    slot: string;
    file?: File;
    deleteImage?: boolean;
    imageName?: string;
  },
): Promise<any> {
  const email = normalizeOperatorEmail(viewerEmail);
  const fd = new FormData();
  fd.append("email", email);
  fd.append("slot", opts.slot);
  if (opts.imageName) fd.append("imageName", opts.imageName);
  if (opts.deleteImage) fd.append("delete", "true");
  if (opts.file) fd.append("file", opts.file);
  const { data } = await instance.post(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/operator-instruction-image`,
    fd,
  );
  return data;
}

export async function updateClientTrackerDepartment(
  teamId: string,
  departmentId: string,
  viewerEmail: string,
  payload: Record<string, unknown>,
): Promise<any> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.put(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/departments/${encodeURIComponent(departmentId)}`,
    { ...payload, email },
  );
  return data;
}

export async function saveClientTrackerContact(
  teamId: string,
  viewerEmail: string,
  payload: Record<string, unknown>,
  contactId?: string,
): Promise<any> {
  const email = normalizeOperatorEmail(viewerEmail);
  // Pass viewer identity via query — never overlay body.email (that is the contact's email).
  const qs = `?email=${encodeURIComponent(email)}`;
  if (contactId) {
    const { data } = await instance.put(
      `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/contacts/${encodeURIComponent(contactId)}${qs}`,
      payload,
    );
    return data;
  }
  const { data } = await instance.post(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/contacts${qs}`,
    payload,
  );
  return data;
}

export async function createClientTrackerUpdate(
  teamId: string,
  viewerEmail: string,
  payload: Record<string, unknown>,
): Promise<any> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.post(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/updates`,
    { ...payload, email },
  );
  return data;
}

export async function logClientTrackerMeeting(
  teamId: string,
  viewerEmail: string,
  payload: Record<string, unknown>,
): Promise<any> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.post(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/meetings`,
    { ...payload, email },
  );
  return data;
}

export type ClientTrackerUseCase = {
  key: string;
  label: string;
  text: string;
  status: string;
  date: string;
};

export type ClientTrackerBudgetDepartment = "clientServices" | "marketing" | "operations";

export type ClientTrackerBudgetExpense = {
  id: string;
  department: ClientTrackerBudgetDepartment;
  title: string;
  amount: number;
  date: string;
  kind: string;
  notes: string;
};

export type ClientTrackerBudget = {
  year: number;
  totals: Record<ClientTrackerBudgetDepartment, number>;
  expenses: ClientTrackerBudgetExpense[];
};

export async function getClientTrackerBudget(
  teamId: string,
  viewerEmail: string,
  year: number,
): Promise<ClientTrackerBudget> {
  const email = normalizeOperatorEmail(viewerEmail);
  const path = `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/budget`;
  const { data } = await instance.get<ClientTrackerBudget>(
    `${path}?year=${year}&email=${encodeURIComponent(email)}`,
  );
  return data;
}

export async function saveClientTrackerBudget(
  teamId: string,
  viewerEmail: string,
  budget: ClientTrackerBudget,
): Promise<ClientTrackerBudget> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.put<ClientTrackerBudget>(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/budget`,
    { ...budget, email },
  );
  return data;
}

export async function saveClientTrackerUseCases(
  teamId: string,
  viewerEmail: string,
  items: ClientTrackerUseCase[],
): Promise<ClientTrackerUseCase[]> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.put<{ items: ClientTrackerUseCase[] }>(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/use-cases`,
    { items, email },
  );
  return data.items || [];
}

export async function saveClientTrackerGoal(
  teamId: string,
  viewerEmail: string,
  payload: Record<string, unknown>,
  goalId?: string,
): Promise<any> {
  const email = normalizeOperatorEmail(viewerEmail);
  if (goalId) {
    const { data } = await instance.put(
      `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/goals/${encodeURIComponent(goalId)}`,
      { ...payload, email },
    );
    return data;
  }
  const { data } = await instance.post(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/goals`,
    { ...payload, email },
  );
  return data;
}

export async function listClientTrackerContacts(
  viewerEmail: string,
  filters?: { search?: string; teamId?: string; includeInactive?: boolean },
): Promise<any[]> {
  const email = normalizeOperatorEmail(viewerEmail);
  const params = new URLSearchParams({ email });
  if (filters?.search) params.set("search", filters.search);
  if (filters?.teamId) params.set("teamId", filters.teamId);
  if (filters?.includeInactive) params.set("includeInactive", "true");
  const { data } = await instance.get<{ contacts: any[] }>(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/contacts?${params.toString()}`,
  );
  return data.contacts || [];
}

export async function createClientTrackerDepartment(
  teamId: string,
  viewerEmail: string,
  name: string,
): Promise<any> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.post(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/departments`,
    { name, email },
  );
  return data;
}

export async function removeClientTrackerDepartment(
  teamId: string,
  departmentId: string,
  viewerEmail: string,
): Promise<any> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.delete(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/departments/${encodeURIComponent(departmentId)}?email=${encodeURIComponent(email)}`,
  );
  return data;
}

export async function restoreClientTrackerDefaultDepartment(
  teamId: string,
  viewerEmail: string,
  name: string,
): Promise<any> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.post(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/departments/restore-default`,
    { name, email },
  );
  return data;
}

export async function reorderClientTrackerDepartments(
  teamId: string,
  viewerEmail: string,
  departmentIds: string[],
): Promise<any[]> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.post<{ departments: any[] }>(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/departments/reorder`,
    { departmentIds, email },
  );
  return data.departments || [];
}

export async function updateClientTrackerBoardConfig(
  teamId: string,
  viewerEmail: string,
  payload: Record<string, unknown>,
): Promise<any> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.put(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/board-config`,
    { ...payload, email },
  );
  return data;
}

export async function presignClientTrackerFileUpload(
  teamId: string,
  viewerEmail: string,
  fileName: string,
  fileMimeType: string,
): Promise<{ uploadURL: string; uploadKey: string }> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.post<{ uploadURL: string; uploadKey: string }>(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/files/presign`,
    { fileName, fileMimeType, email },
  );
  return data;
}

export async function saveClientTrackerFile(
  teamId: string,
  viewerEmail: string,
  payload: Record<string, unknown>,
): Promise<any> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.post(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/files`,
    { ...payload, email },
  );
  return data;
}

export async function archiveClientTrackerFile(
  teamId: string,
  fileId: string,
  viewerEmail: string,
): Promise<any> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.delete(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/files/${encodeURIComponent(fileId)}?email=${encodeURIComponent(email)}`,
  );
  return data;
}

export async function saveClientTrackerOpportunity(
  teamId: string,
  viewerEmail: string,
  payload: Record<string, unknown>,
  opportunityId?: string,
): Promise<any> {
  const email = normalizeOperatorEmail(viewerEmail);
  if (opportunityId) {
    const { data } = await instance.put(
      `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/opportunities/${encodeURIComponent(opportunityId)}`,
      { ...payload, email },
    );
    return data;
  }
  const { data } = await instance.post(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/opportunities`,
    { ...payload, email },
  );
  return data;
}

export async function archiveClientTrackerOpportunity(
  teamId: string,
  opportunityId: string,
  viewerEmail: string,
): Promise<any> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.delete(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/opportunities/${encodeURIComponent(opportunityId)}?email=${encodeURIComponent(email)}`,
  );
  return data;
}

export async function saveClientTrackerPromotion(
  teamId: string,
  viewerEmail: string,
  payload: Record<string, unknown>,
  promotionId?: string,
): Promise<any> {
  const email = normalizeOperatorEmail(viewerEmail);
  if (promotionId) {
    const { data } = await instance.put(
      `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/promotions/${encodeURIComponent(promotionId)}`,
      { ...payload, email },
    );
    return data;
  }
  const { data } = await instance.post(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/promotions`,
    { ...payload, email },
  );
  return data;
}

export async function archiveClientTrackerPromotion(
  teamId: string,
  promotionId: string,
  viewerEmail: string,
): Promise<any> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.delete(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/promotions/${encodeURIComponent(promotionId)}?email=${encodeURIComponent(email)}`,
  );
  return data;
}

export async function updateClientTrackerUpdate(
  teamId: string,
  updateId: string,
  viewerEmail: string,
  payload: Record<string, unknown>,
): Promise<any> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.put(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/updates/${encodeURIComponent(updateId)}`,
    { ...payload, email },
  );
  return data;
}

export type ClientTrackerPinnedAlbum = "image" | "ticket" | "gif";

export type ClientTrackerPinnedAsset = {
  downloadId: string;
  album: ClientTrackerPinnedAlbum;
  gameId: string;
  gameLabel: string;
  startDate?: number;
  pinnedOrder: number;
  s3Key: string;
  previewUrl?: string;
  downloadUrl?: string;
  created?: string;
  email?: string;
  section?: string;
  row?: string;
  seat?: string;
  fileName?: string;
};

function triggerBlobDownload(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
}

async function downloadFromSignedUrl(url: string, filename: string): Promise<void> {
  try {
    const s3Response = await fetch(url);
    if (!s3Response.ok) {
      throw new Error(`Failed to download file (${s3Response.status})`);
    }
    triggerBlobDownload(await s3Response.blob(), filename);
  } catch (err) {
    if (err instanceof Error && err.message.startsWith("Failed to download file")) {
      throw err;
    }
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.rel = "noopener noreferrer";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

export async function downloadClientTrackerPinnedAsset(
  teamId: string,
  viewerEmail: string,
  downloadId: string,
  fileName?: string,
): Promise<void> {
  const email = normalizeOperatorEmail(viewerEmail);
  try {
    const { data } = await instance.get<{ url: string; filename: string }>(
      `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/pinned-assets/${encodeURIComponent(downloadId)}/download?email=${encodeURIComponent(email)}`,
    );
    const url = data?.url;
    if (!url) throw new Error("Download URL missing");
    await downloadFromSignedUrl(url, data.filename || fileName || "download");
  } catch (err) {
    const { response } = err as IAxiosError;
    if (response) throw new Error(getError(response));
    throw err instanceof Error ? err : new Error("Download failed");
  }
}

export async function downloadClientTrackerPinnedAssetsZip(
  teamId: string,
  viewerEmail: string,
  downloadIds?: string[],
): Promise<void> {
  const email = normalizeOperatorEmail(viewerEmail);
  try {
    const { data } = await instance.post<{ url: string; filename: string }>(
      `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/pinned-assets/download-zip`,
      { downloadIds, email },
    );
    const url = data?.url;
    if (!url) throw new Error("Download URL missing");
    await downloadFromSignedUrl(url, data.filename || "pinned-photos-tickets.zip");
  } catch (err) {
    const { response } = err as IAxiosError;
    if (response) throw new Error(getError(response));
    throw err instanceof Error ? err : new Error("Download failed");
  }
}

export async function listClientTrackerPinnedAssets(
  teamId: string,
  viewerEmail: string,
): Promise<ClientTrackerPinnedAsset[]> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.get<{ items: ClientTrackerPinnedAsset[] }>(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/pinned-assets?email=${encodeURIComponent(email)}`,
  );
  return data.items || [];
}

export async function setClientTrackerPinnedAsset(
  teamId: string,
  viewerEmail: string,
  payload: {
    gameId: string;
    downloadId: string;
    album: ClientTrackerPinnedAlbum;
    pinned: boolean;
  },
): Promise<void> {
  const email = normalizeOperatorEmail(viewerEmail);
  await instance.put(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/pinned-assets/pin`,
    { ...payload, email },
  );
}

export async function uploadClientTrackerPinnedAssets(
  teamId: string,
  viewerEmail: string,
  gameId: string,
  album: ClientTrackerPinnedAlbum,
  files: File[],
): Promise<{ added: number }> {
  const email = normalizeOperatorEmail(viewerEmail);
  const pathBase = `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/pinned-assets`;
  const uploadKeys: string[] = [];
  try {
    await files.reduce<Promise<void>>(
      (prev, file) => prev.then(async () => {
        const mime = album === "gif"
          ? "image/gif"
          : (file.type || "application/octet-stream");
        const { data: presign } = await instance.post<{ uploadKey: string; uploadURL: string }>(
          `${pathBase}/presign`,
          {
            email,
            gameId,
            album,
            fileName: file.name,
            fileMimeType: mime,
          },
        );
        const putRes = await axios.put(presign.uploadURL, file, {
          headers: { "Content-Type": mime },
        });
        if (putRes.status < 200 || putRes.status >= 300) {
          throw new Error(`Upload failed for ${file.name}`);
        }
        uploadKeys.push(presign.uploadKey);
      }),
      Promise.resolve(),
    );
    const { data } = await instance.post<{ added: number }>(
      `${pathBase}/register`,
      { email, gameId, album, uploadKeys },
    );
    return { added: data.added || uploadKeys.length };
  } catch (err) {
    const { response } = err as IAxiosError;
    if (response) throw new Error(getError(response));
    throw err instanceof Error ? err : new Error("Upload failed");
  }
}

export async function listClientTrackerRecaps(
  teamId: string,
  viewerEmail: string,
): Promise<import("./recap").GameRecapShareListItem[]> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.get<{ shares: import("./recap").GameRecapShareListItem[] }>(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/recaps?email=${encodeURIComponent(email)}`);
  return data.shares || [];
}

export async function updateClientTrackerRecap(
  teamId: string,
  shareId: string,
  viewerEmail: string,
  patch: {
    refreshMode?: "auto" | "manual";
    includeExtraPinnedPhotos?: boolean;
    title?: string;
    pushRefresh?: boolean;
  },
): Promise<import("./recap").GameRecapShareListItem> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.put<{ share: import("./recap").GameRecapShareListItem }>(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/recaps/${encodeURIComponent(shareId)}`,
  { ...patch, email });
  return data.share;
}

export async function deleteClientTrackerRecap(
  teamId: string,
  shareId: string,
  viewerEmail: string,
): Promise<void> {
  const email = normalizeOperatorEmail(viewerEmail);
  await instance.delete(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/recaps/${encodeURIComponent(shareId)}?email=${encodeURIComponent(email)}`,
  );
}

export type ClientTrackerDeliverableAsset = {
  id: string;
  fileName: string;
  s3Key: string;
  url?: string;
};

export type ClientTrackerDeliverableOffering = {
  key: string;
  label: string;
  enabled: boolean;
  imageKey: string;
  imageFileName: string;
  imageUrl?: string;
};

export type ClientTrackerVenueVision = {
  gender: boolean;
  age: boolean;
  emptySeatFlow: boolean;
  jerseyBreakdown: boolean;
  awayFans: boolean;
  attentionTracking: boolean;
  sentimentAnalysis: boolean;
  sigmaDashboard: boolean;
  sigmaDashboardLink: string;
  sigmaDashboardAccess: string;
};

export type ClientTrackerFutureSnapsEvent = {
  id: string;
  name: string;
  eventDateMs: number;
  dateLabel: string;
  activation: string;
  memberCount: number;
  arrivalTime?: string;
};

export type ClientTrackerDeliverables = {
  dataDeliveryDates: string;
  dataDeliveryFrequency: string;
  attentionTrackingDates: string;
  venueVision: ClientTrackerVenueVision;
  snapSquadInformation: string;
  snapsOfferings: ClientTrackerDeliverableOffering[];
  snapsIdeas: string;
  snapsLocationImages: ClientTrackerDeliverableAsset[];
  futureSnapsEvents: ClientTrackerFutureSnapsEvent[];
  sponsor: {
    name: string;
    logoUrl?: string;
    deliverables: string;
    distinction: string;
    approvedAssets: ClientTrackerDeliverableAsset[];
    restrictedAssets: ClientTrackerDeliverableAsset[];
  };
};

function deliverablesPath(teamId: string): string {
  return `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/deliverables`;
}

export async function getClientTrackerDeliverables(
  teamId: string,
  viewerEmail: string,
): Promise<ClientTrackerDeliverables> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.get<ClientTrackerDeliverables>(
    `${deliverablesPath(teamId)}?email=${encodeURIComponent(email)}`,
  );
  return data;
}

export async function saveClientTrackerDeliverables(
  teamId: string,
  viewerEmail: string,
  payload: Record<string, unknown>,
): Promise<ClientTrackerDeliverables> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.put<ClientTrackerDeliverables>(
    deliverablesPath(teamId),
    { ...payload, email },
  );
  return data;
}

export async function presignClientTrackerDeliverableUpload(
  teamId: string,
  viewerEmail: string,
  fileName: string,
  fileMimeType: string,
): Promise<{ uploadURL: string; uploadKey: string }> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.post<{ uploadURL: string; uploadKey: string }>(
    `${deliverablesPath(teamId)}/presign`,
    { fileName, fileMimeType, email },
  );
  return data;
}

export type PromotionalPlanItem = {
  key: string;
  label: string;
  custom: boolean;
  enabled: boolean;
  notes: string;
  date: string;
  imageKey: string;
  imageFileName: string;
  imageUrl?: string;
  linkedPromotionId: string;
  influencerList: string;
  spend: string;
  extra: "none" | "influencers" | "spend";
};

export type PromotionalPlan = {
  overview: string;
  items: PromotionalPlanItem[];
};

function promotionalPlanPath(teamId: string): string {
  return `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/promotional-plan`;
}

export async function getClientTrackerPromotionalPlan(
  teamId: string,
  viewerEmail: string,
): Promise<PromotionalPlan> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.get<PromotionalPlan>(
    `${promotionalPlanPath(teamId)}?email=${encodeURIComponent(email)}`,
  );
  return data;
}

export async function saveClientTrackerPromotionalPlan(
  teamId: string,
  viewerEmail: string,
  payload: { overview: string; items: PromotionalPlanItem[] },
): Promise<PromotionalPlan> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.put<PromotionalPlan>(
    promotionalPlanPath(teamId),
    { ...payload, email },
  );
  return data;
}

export async function listClientTrackerRecapViewers(
  teamId: string,
  shareId: string,
  viewerEmail: string,
): Promise<import("./recap").GameRecapShareViewer[]> {
  const email = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.get<{ viewers: import("./recap").GameRecapShareViewer[] }>(
    `${PATH.API}/${PATH.OPERATOR}/client-tracker/${encodeURIComponent(teamId)}/recaps/${encodeURIComponent(shareId)}/viewers?email=${encodeURIComponent(email)}`);
  return data.viewers || [];
}
